import {
  computed, nextTick, ref,
} from '@vue/composition-api';
import moment from 'moment';
import { useI18n } from 'vue-i18n-composable';
import SystemNotification from '@/orm/models/SystemNotification/SystemNotification';
import SystemNotificationSettings from '@/orm/models/SystemNotification/SystemNotificationSettings';
import { buildNotificationTextParts } from '@/helpers/systemNotifications';

const PER_PAGE = 20;
const JUST_NOW_THRESHOLD_SECONDS = 60;
const justReceivedNotificationIds = ref(new Set());
const justReceivedVersion = ref(0);

export function markSystemNotificationJustReceived(id) {
  if (!id) {
    return;
  }

  justReceivedNotificationIds.value.add(id);
  justReceivedVersion.value += 1;

  setTimeout(() => {
    justReceivedNotificationIds.value.delete(id);
    justReceivedVersion.value += 1;
  }, JUST_NOW_THRESHOLD_SECONDS * 1000);
}

function formatNotificationDate(notification, locale, justReceivedIds, t) {
  if (justReceivedIds.includes(notification.id)) {
    return t('systemNotifications.justNow');
  }

  const createdAt = moment(notification.createdAt);

  if (createdAt.isValid() && moment().diff(createdAt, 'seconds') < JUST_NOW_THRESHOLD_SECONDS) {
    return t('systemNotifications.justNow');
  }

  return createdAt.locale(locale).fromNow();
}

function mapNotificationForDisplay(notification, locale, justReceivedIds, t) {
  return {
    id: notification.id,
    read: SystemNotification.isRead(notification),
    header: notification.notificationHeader,
    textParts: buildNotificationTextParts(notification),
    dateLabel: formatNotificationDate(notification, locale, justReceivedIds, t),
  };
}

export default function useSystemNotifications() {
  const { locale, t } = useI18n();
  const isLoading = ref(true);
  const isLoadingMore = ref(false);

  const notifications = computed(() => SystemNotification.all());
  const metaData = computed(() => SystemNotification.getMetaData());
  const unreadCount = computed(() => SystemNotification.getUnreadCount());
  const hasMore = computed(() => {
    const { currentPage, lastPage } = metaData.value;
    return lastPage > 0 && currentPage < lastPage;
  });

  const justReceivedIdsSnapshot = computed(() => (
    justReceivedVersion.value >= 0 ? [...justReceivedNotificationIds.value] : []
  ));

  const displayNotifications = computed(() => notifications.value
    .map((notification) => mapNotificationForDisplay(
      notification,
      locale.value.toLowerCase(),
      justReceivedIdsSnapshot.value,
      t,
    ))
    .sort((a, b) => {
      if (a.read !== b.read) {
        return Number(a.read) - Number(b.read);
      }

      const dateA = SystemNotification.find(a.id)?.createdAt;
      const dateB = SystemNotification.find(b.id)?.createdAt;

      return moment(dateB).valueOf() - moment(dateA).valueOf();
    }));

  const settings = computed(() => SystemNotificationSettings.query().first());

  async function fetchNotifications(page = 1) {
    if (page === 1) {
      return initializePage();
    }

    isLoadingMore.value = true;

    try {
      await SystemNotification.index({ page, perPage: PER_PAGE });
    } finally {
      isLoadingMore.value = false;
    }
  }

  async function initializePage() {
    isLoading.value = true;

    try {
      await SystemNotification.index({ page: 1, perPage: PER_PAGE });
    } finally {
      isLoading.value = false;
    }

    await nextTick();
    await markAllAsRead();
  }

  async function loadMore() {
    if (isLoadingMore.value || isLoading.value || !hasMore.value) {
      return;
    }

    const nextPage = (metaData.value.currentPage || 0) + 1;
    await fetchNotifications(nextPage);
  }

  async function markAllAsRead() {
    const viewedAt = new Date().toISOString();

    await SystemNotification.api().markAsRead();
    await SystemNotification.update({
      where: () => true,
      data: { viewedAt },
    });
    SystemNotification.setUnreadCount(0);
  }

  async function finalizeNotificationsPage() {
    await markAllAsRead();
  }

  async function fetchSettings() {
    const response = await SystemNotificationSettings.api().fetchSettings();
    const data = response.response?.data?.data || {};

    await SystemNotificationSettings.insertOrUpdate({
      data: {
        id: 1,
        ...data,
      },
    });
  }

  async function updateSettings(payload) {
    const response = await SystemNotificationSettings.api().updateSettings(payload);
    const responseData = response.response?.data?.data;

    await SystemNotificationSettings.insertOrUpdate({
      data: {
        id: 1,
        receiveEmailNotifications: responseData?.receiveEmailNotifications ?? payload.receiveEmailNotifications,
      },
    });
  }

  return {
    displayNotifications,
    unreadCount,
    hasMore,
    isLoading,
    isLoadingMore,
    settings,
    initializePage,
    loadMore,
    finalizeNotificationsPage,
    fetchSettings,
    updateSettings,
  };
}
